import { ProductTypeValue } from '@app/data-stores/ptr/types';
import { usePrefersReducedMotion } from '@app/hooks/usePrefersReducedMotion';
import { CurrentConfiguratorValues } from '@app/pages/i/hooks/usePTRProductConfigurator';
import { DeepReadonly } from '@app/types';
import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import Form from '@redbubble/design-system/src/Form';
import ChevronDownBigIcon from '@redbubble/design-system/src/Icons/ChevronDownBig';
import ChevronUpBigIcon from '@redbubble/design-system/src/Icons/ChevronUpBig';
import Skeleton from '@redbubble/design-system/src/Skeleton';
import Text from '@redbubble/design-system/src/Text';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { ExperimentConfig } from '@redbubble/next-amplitude';
import { useAmplitudeExperiment } from '@redbubble/next-provider/amplitude';
import Color from 'color';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingBoundary } from '../../LoadingBoundary';
import { ProductConfiguratorFields } from '..';
import { ColorOption } from '../FieldConfigurators/types';
import { DSFormOnChangeCallback } from '../types';
import { useConfiguratorViewportProfile } from '../useConfiguratorViewportProfile';
import { ConfiguratorOption, getColorOptionConfig } from '../utils';
import styles from './CollapsibleColorPicker.module.scss';

type SwatchesResult = {
  swatches: ColorOption[];
  numberOfOtherAvailableColors: number;
};

export const messages = defineMessages({
  colors: {
    defaultMessage: `{availableColors, plural,
        =0 {see color}
        one {+{availableColors} color}
        other {+{availableColors} colors}}
      `,
  },
  colorsButtonAriaLabel: {
    defaultMessage: `See {availableColors, plural,
      one {{availableColors} available color}
      other {{availableColors} available colors}}
    `,
  },
});

export interface CollapsibleColorPickerProps {
  colorConfiguratorOption: ConfiguratorOption;
  productType: DeepReadonly<ProductTypeValue>;
  currentValues: CurrentConfiguratorValues;
  onConfiguratorFormChange: DSFormOnChangeCallback;
  locale: string;
}

const getHexString = (hexColor: string): string =>
  hexColor.startsWith('#') ? hexColor : `#${hexColor}`;

const isLight = (hexColor: string) => {
  return Color(getHexString(hexColor)).luminosity() > 0.8;
};

const MAX_SWATCHES = 4;

const getSwatches = (
  currentValue: string,
  colorsData: ColorOption[]
): SwatchesResult => {
  const currentColorData = colorsData.find(
    (color) => color.value === currentValue
  );

  const otherColorsData = colorsData.filter(
    (color) => color.value !== currentValue
  );

  let swatches: ColorOption[];
  if (currentColorData) {
    swatches =
      colorsData.length > MAX_SWATCHES
        ? [currentColorData, ...otherColorsData.slice(0, MAX_SWATCHES - 1)]
        : [currentColorData];
  } else {
    swatches = colorsData.slice(0, MAX_SWATCHES);
  }

  const numberOfOtherAvailableColors = colorsData.length - swatches.length;

  return { swatches, numberOfOtherAvailableColors };
};

export const CollapsibleColorPicker: React.FC<CollapsibleColorPickerProps> = ({
  colorConfiguratorOption,
  currentValues,
  onConfiguratorFormChange,
  locale,
}) => {
  const intl = useIntl();
  const isSwatchExpTreatment =
    useAmplitudeExperiment(ExperimentConfig.PdpMobileColorSwatchVisibility) ===
    'treatment';
  const collapsibleRef = useRef<HTMLDivElement>(null);
  const [collapsibleOpen, setCollapsibleOpen] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const currentValue = useMemo(
    () =>
      currentValues.prefixedFamilyOptionsValues[colorConfiguratorOption.key] ||
      currentValues.prefixedProductOptionsValues[colorConfiguratorOption.key] ||
      '',
    [currentValues, colorConfiguratorOption]
  );
  const initialFormValues = useMemo(
    () => ({ [colorConfiguratorOption.key]: currentValue }),
    [colorConfiguratorOption, currentValue]
  );

  useEffect(() => {
    if (collapsibleRef.current && collapsibleOpen) {
      collapsibleRef.current.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'center',
      });
    }
  }, [collapsibleOpen, prefersReducedMotion]);

  const handleCollapsibleClick = () => {
    setCollapsibleOpen(!collapsibleOpen);
  };

  const viewportProfile = useConfiguratorViewportProfile();

  const colorOptions = useMemo((): ColorOption[] => {
    return colorConfiguratorOption.filterValues
      .map(({ value }) => getColorOptionConfig(value, locale))
      .filter((opt) => opt !== null);
  }, [colorConfiguratorOption.filterValues, locale]);

  const { swatches, numberOfOtherAvailableColors } = getSwatches(
    currentValue,
    colorOptions
  );

  const availableColorsMessage = intl.formatMessage(messages.colors, {
    availableColors: numberOfOtherAvailableColors,
  });

  const colorsButtonAriaLabel = intl.formatMessage(
    messages.colorsButtonAriaLabel,
    {
      availableColors: colorOptions.length,
    }
  );

  const getCollapsibleClasses = () => {
    const baseClasses = [styles.collapsibleColorsSection];
    if (isSwatchExpTreatment) {
      baseClasses.push(styles.expandedSwatchExp);
    }
    if (collapsibleOpen) {
      baseClasses.push(styles.open);
    }
    if (!prefersReducedMotion && !isSwatchExpTreatment) {
      baseClasses.push(styles.animated);
    }
    return baseClasses.join(' ');
  };

  return (
    <LoadingBoundary
      loadingComponent={<Skeleton height={32} width={191} animated />}
    >
      {!isSwatchExpTreatment && (
        <Button
          type="button"
          intent={constants.PRIMARY}
          size={constants.SMALL}
          onClick={handleCollapsibleClick}
          iconAfter={
            collapsibleOpen ? <ChevronUpBigIcon /> : <ChevronDownBigIcon />
          }
          aria-expanded={!collapsibleOpen}
          aria-label={colorsButtonAriaLabel}
          data-testid="collapsible-color-picker"
        >
          <Box display="flex" paddingRight="xs" ref={collapsibleRef}>
            {swatches.map(({ hexColor }) => (
              <Box
                key={hexColor}
                className={styles.colorSwatch}
                style={{
                  backgroundColor: getHexString(hexColor),
                  border: isLight(getHexString(hexColor))
                    ? `2px solid ${constants.COLOR_NEUTRAL_200}`
                    : 'none',
                }}
                data-testid="color-swatch"
              />
            ))}
          </Box>
          <Text type="display6">{availableColorsMessage}</Text>
        </Button>
      )}

      <Box className={getCollapsibleClasses()}>
        <Form
          enableReinitialize
          initialValues={initialFormValues}
          onChange={onConfiguratorFormChange}
          profile={viewportProfile}
        >
          {({ errors }) => (
            <ProductConfiguratorFields
              configuratorOptions={[colorConfiguratorOption]}
              currentValues={currentValues}
              locale={locale}
              errors={errors}
            />
          )}
        </Form>
      </Box>
    </LoadingBoundary>
  );
};
